<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAccount } from 'dashboard/composables/useAccount';
import { useAlert } from 'dashboard/composables';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SectionLayout from './SectionLayout.vue';
import Switch from 'next/switch/Switch.vue';
import Checkbox from 'dashboard/components-next/checkbox/Checkbox.vue';

const { t } = useI18n();
const isEnabled = ref(false);
const consent = ref(false);

const { currentAccount, updateAccount } = useAccount();

watch(
  currentAccount,
  () => {
    const enabled = !!currentAccount.value?.features?.[FEATURE_FLAGS.IP_LOOKUP];
    isEnabled.value = enabled;
    consent.value = enabled;
  },
  { deep: true, immediate: true }
);

const switchDisabled = computed(() => {
  if (isEnabled.value) return false;
  return !consent.value;
});

const updateIpLookup = async () => {
  const nextValue = isEnabled.value;
  try {
    await updateAccount({
      ip_lookup: nextValue,
      ...(nextValue ? { ip_lookup_consent: true } : {}),
    });
    useAlert(t('GENERAL_SETTINGS.FORM.IP_LOOKUP.API.SUCCESS'));
    if (!nextValue) consent.value = false;
  } catch {
    isEnabled.value = !nextValue;
    useAlert(t('GENERAL_SETTINGS.FORM.IP_LOOKUP.API.ERROR'));
  }
};
</script>

<template>
  <SectionLayout
    :title="t('GENERAL_SETTINGS.FORM.IP_LOOKUP.TITLE')"
    :description="t('GENERAL_SETTINGS.FORM.IP_LOOKUP.NOTE')"
    with-border
  >
    <template #headerActions>
      <div class="flex justify-end">
        <Switch
          v-model="isEnabled"
          :disabled="switchDisabled"
          @change="updateIpLookup"
        />
      </div>
    </template>
    <p class="text-n-slate-11 text-body-main">
      {{ t('GENERAL_SETTINGS.FORM.IP_LOOKUP.DISCLAIMER') }}
    </p>
    <label
      v-if="!isEnabled"
      class="flex items-start gap-2 mt-4 cursor-pointer"
    >
      <Checkbox v-model="consent" class="mt-0.5" />
      <span class="text-n-slate-12 text-body-main">
        {{ t('GENERAL_SETTINGS.FORM.IP_LOOKUP.CONSENT') }}
      </span>
    </label>
  </SectionLayout>
</template>
